// frontend/src/pages/portal/PortalLayout.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import ChatPanel from '../../components/portal/ChatPanel';
import { HELP_PAGE_MAPPING } from '../../data/helpContent';
import FeatureLocked from '../../components/portal/FeatureLocked';
import { FEATURE_PLAN } from '../../constants/featureCatalogue';
import useIsMobile from '../../hooks/useIsMobile';
import MobileTabBar, { MOBILE_TAB_BAR_HEIGHT } from '../../components/common/MobileTabBar';
import ConnectionBadge from '../../components/common/ConnectionBadge';
import { C } from '../../styles/theme';
import { useTheme } from '../../context/ThemeContext';
import '../../styles/responsive.css';

// Role label is now derived from the role_name stored at login

const SIDEBAR_EXPANDED_W = 200;
const SIDEBAR_COLLAPSED_W = 60;

const NAV_GROUPS = [
    {
        label: 'OPERATIONS',
        items: [
            {
                id: 'overview', label: 'Dashboard', path: '/portal/overview',
                icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>),
            },
            {
                id: 'reservations', label: 'Reservations', path: '/portal/reservations',
                icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>),
            },
            {
                id: 'expenses', label: 'Expenses', path: '/portal/expenses',
                icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>),
            },
            {
                id: 'end-of-day', label: 'End of Day', path: '/portal/end-of-day',
                icon: (<svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>),
            },
            // 'voids' nav item removed — now reachable via a tab strip on Transactions (Task 2)
        ],
    },
    {
        label: 'INSIGHTS',
        items: [
            {
                id: 'transactions', label: 'Transactions', path: '/portal/transactions',
                icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 2v20l4-2 4 2 4-2 4 2V2l-4 2-4-2-4 2z"/><line x1="8" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="12" y2="14"/></svg>),
            },
            {
                id: 'reporting', label: 'Analytics', path: '/portal/reporting',
                icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>),
            },
            {
                id: 'marketing', label: 'Marketing', path: '/portal/marketing',
                icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>),
            },
        ],
    },
    {
        label: 'HR',
        items: [
            {
                id: 'employees', label: 'Employees', path: '/portal/employees',
                icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>),
            },
            {
                id: 'payroll', label: 'Pay Run', path: '/portal/payrun',
                icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>),
            },
            {
                id: 'schedule', label: 'Schedule', path: '/portal/schedule',
                icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>),
            },
        ],
    },
    {
        label: 'CATALOGUE',
        items: [
            {
                id: 'products', label: 'Products', path: '/portal/products',
                icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>),
            },
            {
                id: 'inventory', label: 'Inventory', path: '/portal/inventory',
                icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>),
            },
        ],
    },
    // 'SUPPORT' group removed — Help Center stays reachable via the existing topbar icon
    {
        label: 'ADMIN',
        items: [
            // 'roles' and 'devices' nav items removed — now reachable via Settings tabs (Task 2)
            {
                id: 'settings', label: 'Settings', path: '/portal/settings',
                icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>),
            },
        ],
    },
];

// NOTE: 'roles' and 'devices' no longer have sidebar nav items (moved under Settings),
// and 'voids' no longer has a sidebar nav item (moved under Transactions). Their map
// entries are kept (and exported) so those relocated entry points can import and
// reuse the exact same gate values instead of duplicating them.
export const NAV_FEATURE_MAP = {
    reservations: 'reservations',
    reporting: 'analytics',
    marketing: 'marketing',
    expenses: 'expenses',
    employees: 'hr',
    payroll: 'hr',
    payrun: 'hr',
    schedule: 'hr',
    inventory: 'inventory',
    roles: 'team_access',
    devices: 'devices_multi',
};

export const NAV_PERMISSION_MAP = {
    overview:   'portal:overview',
    reporting:  'portal:reporting',
    expenses:   'portal:expenses',
    employees:  'portal:employees',
    payroll:    'portal:payroll',
    schedule:   'portal:schedule',
    products:   'portal:products',
    inventory:  'portal:inventory',
    marketing:  'portal:marketing',
    roles:      'portal:roles',
    devices:    'portal:devices',
    settings:   'portal:settings',
    reservations: 'portal:overview',
    voids:        'portal:voids',
    transactions: 'portal:reporting',
    'help-center': 'portal:overview',
};

const PAGE_TITLES = {
    overview: 'Dashboard',
    employees: 'Employees',
    payroll: 'Pay Run',
    payrun: 'Pay Run',
    schedule: 'Schedule',
    products: 'Products',
    expenses: 'Expenses',
    reporting: 'Analytics',
    settings: 'Settings',
    marketing: 'Marketing',
    roles: 'Roles & Permissions',
    devices: 'Devices',
    inventory: 'Inventory',
    reservations: 'Reservations',
    'end-of-day': 'End of Day',
    voids: 'Voids',
    transactions: 'Recent Transactions',
    'help-center': 'Help Center',
};

const PortalLayout = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { logout, getBrandName, username, userRole, hasPermission, permissions, hasFeature, enabledFeatures, isLoading } = useAuth();
    const { t, language, toggleLanguage } = useLanguage();
    const isMobile = useIsMobile();
    const [langDropdownOpen, setLangDropdownOpen] = useState(false);
    const { theme, toggleTheme } = useTheme();
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('portal-sidebar-collapsed') === 'true');
    const [chatOpen, setChatOpen] = useState(false);
    const [chatMinimised, setChatMinimised] = useState(false);

    useEffect(() => {
        const handleOpenChat = () => { setChatOpen(true); setChatMinimised(false); };
        window.addEventListener('open-help-chat', handleOpenChat);
        return () => window.removeEventListener('open-help-chat', handleOpenChat);
    }, []);

    const toggleSidebar = useCallback(() => {
        setSidebarCollapsed(prev => {
            const next = !prev;
            localStorage.setItem('portal-sidebar-collapsed', String(next));
            return next;
        });
    }, []);
    const langDropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (langDropdownRef.current && !langDropdownRef.current.contains(e.target)) {
                setLangDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const collapsed = sidebarCollapsed;
    const activeId = location.pathname.split('/portal/')[1]?.split('/')[0] || 'overview';
    const pageTitle = PAGE_TITLES[activeId] || 'Manager Portal';
    const today = new Date().toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
    const roleName = localStorage.getItem('role_name');
    const roleLabel = roleName || userRole || 'Manager';
    const displayName = localStorage.getItem('display_name') || username || 'Admin';
    const userInitial = displayName.charAt(0).toUpperCase();

    // ─── Route Guard ─────────────────────────────────────────────────────────
    const hasAnyPortalAccess = !permissions || permissions.length === 0 ||
        permissions.some(p => p.key?.startsWith('portal:') && p.can_view);

    const currentPageAllowed = (() => {
        if (!permissions || permissions.length === 0) return true; // legacy user
        const permKey = NAV_PERMISSION_MAP[activeId];
        if (!permKey) return true; // no mapping = always allowed
        return permissions.some(p => p.key === permKey && p.can_view);
    })();

    const activeFeature = NAV_FEATURE_MAP[activeId];
    const featurePageLocked = activeFeature && hasFeature && !hasFeature(activeFeature);

    useEffect(() => {
        if (!permissions || permissions.length === 0) return;
        if (currentPageAllowed) return;
        if (!hasAnyPortalAccess) return; // don't redirect — show access denied instead

        // Redirect to first permitted portal page
        const allNavItems = NAV_GROUPS.flatMap(g => g.items);
        const firstPermitted = allNavItems.find(item => {
            const pk = NAV_PERMISSION_MAP[item.id];
            if (!pk) return false;
            return permissions.some(p => p.key === pk && p.can_view);
        });
        if (firstPermitted) {
            navigate(firstPermitted.path, { replace: true });
        }
    }, [activeId, permissions, navigate, currentPageAllowed, hasAnyPortalAccess]);

    // No portal access at all — show access denied screen
    if (!hasAnyPortalAccess) {
        return (
            <div style={{ display: 'flex', height: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif", background: C.bg, alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ textAlign: 'center', maxWidth: 400 }}>
                    <div style={{ width: 64, height: 64, background: C.redLight, borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                        </svg>
                    </div>
                    <h2 style={{ fontSize: 22, fontWeight: 800, color: C.text, margin: '0 0 8px' }}>{t('no_portal_access')}</h2>
                    <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.6, margin: '0 0 24px' }}>
                        {t('no_portal_access_description')}
                    </p>
                    <button onClick={logout} style={{
                        padding: '10px 24px', borderRadius: 8, border: 'none', cursor: 'pointer',
                        background: C.green, color: C.surface, fontSize: 14, fontWeight: 600,
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                    }}>
                        {t('sign_out')}
                    </button>
                </div>
            </div>
        );
    }

    // Same permission/feature-filtered nav data feeds the desktop sidebar AND the
    // mobile tab bar / More sheet — single source of truth for what's gated.
    const filteredNavGroups = NAV_GROUPS
        .map(group => ({
            label: group.label,
            items: group.items
                .filter(item => {
                    const permKey = NAV_PERMISSION_MAP[item.id];
                    if (!permKey) return true;
                    return hasPermission(permKey);
                })
                .map(item => {
                    const featureFlag = NAV_FEATURE_MAP[item.id];
                    return { ...item, featureLocked: !!(featureFlag && hasFeature && !hasFeature(featureFlag)) };
                }),
        }))
        .filter(group => group.items.length > 0);

    return (
        <div className="portal-root" style={{ display: 'flex', fontFamily: "'Plus Jakarta Sans', sans-serif", background: C.bg }}>

            {/* Sidebar (desktop only — mobile uses the bottom tab bar + More sheet) */}
            {!isMobile && (
            <div style={{
                width: collapsed ? SIDEBAR_COLLAPSED_W : SIDEBAR_EXPANDED_W,
                background: C.sidebar, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', flexShrink: 0,
                transition: 'width 0.2s ease', zIndex: 40,
            }}>

                {/* Brand header */}
                <div style={{ padding: collapsed ? '12px 10px' : '12px 12px 12px 20px', borderBottom: `1px solid ${C.border}`, flexShrink: 0, transition: 'padding 0.2s ease', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ overflow: 'hidden', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <img
                            src="/aqana-mark.png"
                            alt="Aqana"
                            style={{ height: collapsed ? 22 : 28, filter: theme === 'dark' ? 'brightness(0) invert(1)' : 'none', transition: 'height 0.2s ease', flexShrink: 0 }}
                        />
                        {!collapsed && (
                            <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {getBrandName()}
                            </div>
                        )}
                    </div>
                    <div
                        onClick={toggleSidebar}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, cursor: 'pointer', color: C.mutedLight, borderRadius: 5, transition: 'color 0.15s ease, background 0.15s ease', flexShrink: 0 }}
                        onMouseEnter={e => { e.currentTarget.style.color = C.text; e.currentTarget.style.background = C.greenLight; }}
                        onMouseLeave={e => { e.currentTarget.style.color = C.mutedLight; e.currentTarget.style.background = 'transparent'; }}
                        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'transform 0.2s', transform: collapsed ? 'rotate(180deg)' : 'none' }}>
                            <polyline points="15 18 9 12 15 6"/>
                        </svg>
                    </div>
                </div>

                {/* Nav groups */}
                <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', paddingBottom: 8 }}>
                    {filteredNavGroups.map(group => (
                            <div key={group.label}>
                                {!collapsed && (
                                    <div style={{ fontSize: 9, fontWeight: 700, color: C.mutedLight, letterSpacing: 2, textTransform: 'uppercase', padding: '16px 20px 6px' }}>
                                        {t(group.label)}
                                    </div>
                                )}
                                {group.items.map(item => {
                                    const isActive = activeId === item.id;
                                    const featureLocked = item.featureLocked;
                                    return (
                                        <div
                                            key={item.id}
                                            onClick={() => { if (!item.disabled) { navigate(item.path); } }}
                                            title={item.disabled ? t('coming_soon') : t(item.label)}
                                            style={{
                                                display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start',
                                                gap: collapsed ? 0 : 10, padding: collapsed ? '8px 0' : '8px 16px',
                                                margin: collapsed ? '1px 6px' : '1px 8px', borderRadius: 7, cursor: item.disabled ? 'default' : 'pointer',
                                                background: isActive ? C.greenLight : 'transparent',
                                                color: isActive ? C.greenDark : item.disabled ? C.mutedLight : C.sidebarText,
                                                transition: 'background 0.15s ease, color 0.15s ease',
                                            }}
                                            onMouseEnter={e => { if (!item.disabled && !isActive) { e.currentTarget.style.background = C.greenLight; e.currentTarget.style.color = C.text; } }}
                                            onMouseLeave={e => { if (!item.disabled && !isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.sidebarText; } }}
                                        >
                                            <span style={{ flexShrink: 0, display: 'flex' }}>{item.icon}</span>
                                            {!collapsed && (
                                                <span style={{ fontSize: 13, fontWeight: isActive ? 600 : 500, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5 }}>
                                                    {t(item.label)}
                                                    {item.disabled && <span style={{ fontSize: 8, marginLeft: 5, color: C.mutedLight, fontWeight: 600, letterSpacing: 0.5 }}>{t('soon_badge')}</span>}
                                                    {featureLocked && !item.disabled && (
                                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: C.mutedLight, flexShrink: 0 }}>
                                                            <rect x="3" y="11" width="18" height="11" rx="2"/>
                                                            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                                                        </svg>
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                    ))}
                </nav>

                {/* Sign out */}
                <div style={{ padding: '8px', borderTop: `1px solid ${C.border}` }}>
                    <div
                        onClick={logout}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start', gap: collapsed ? 0 : 10, padding: '9px 12px', cursor: 'pointer', color: C.muted, borderRadius: 7, transition: 'color 0.15s ease, background 0.15s ease' }}
                        onMouseEnter={e => { e.currentTarget.style.color = C.red; e.currentTarget.style.background = C.greenLight; }}
                        onMouseLeave={e => { e.currentTarget.style.color = C.muted; e.currentTarget.style.background = 'transparent'; }}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
                        </svg>
                        {!collapsed && <span style={{ fontSize: 13, fontWeight: 500 }}>{t('sign_out')}</span>}
                    </div>
                </div>
            </div>
            )}

            {/* Main content */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

                {/* Top bar */}
                <div style={{ height: 52, background: C.surface, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: isMobile ? '0 12px' : '0 28px', flexShrink: 0, gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                        <h1 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t(pageTitle)}</h1>
                        {!isMobile && <span style={{ fontSize: 11, fontWeight: 600, color: C.muted, background: C.slateLight, borderRadius: 5, padding: '2px 8px' }}>{today}</span>}
                        {!isMobile && HELP_PAGE_MAPPING[activeId] && activeId !== 'help-center' && (
                            <div
                                onClick={() => navigate(`/portal/help-center?article=${HELP_PAGE_MAPPING[activeId]}`)}
                                title={t('help_learn_about') + ' ' + pageTitle}
                                style={{ cursor: 'pointer', color: C.mutedLight, display: 'flex', alignItems: 'center', padding: 4, borderRadius: 6, transition: 'color 0.15s, background 0.15s' }}
                                onMouseEnter={e => { e.currentTarget.style.color = C.green; e.currentTarget.style.background = C.greenLight; }}
                                onMouseLeave={e => { e.currentTarget.style.color = C.mutedLight; e.currentTarget.style.background = 'transparent'; }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                                </svg>
                            </div>
                        )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <ConnectionBadge />
                        <button onClick={toggleTheme} title={theme === 'dark' ? t('light_mode') : t('dark_mode')} aria-label="Toggle theme" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, display: 'flex', alignItems: 'center', color: C.muted }}>
                            {theme === 'dark'
                                ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
                                : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>}
                        </button>
                        <div ref={langDropdownRef} style={{ position: 'relative' }}>
                            <button
                                onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                                style={{
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    padding: 6, borderRadius: 6, display: 'flex',
                                    alignItems: 'center', color: C.muted,
                                }}
                                title="Language"
                            >
                                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10"/>
                                    <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                                </svg>
                            </button>
                            {langDropdownOpen && (
                                <div style={{
                                    position: 'absolute', top: '100%', right: 0, marginTop: 4,
                                    background: C.surface, border: `1px solid ${C.border}`,
                                    borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                    minWidth: 140, zIndex: 999, overflow: 'hidden',
                                }}>
                                    <button
                                        onClick={() => { if (language !== 'en') toggleLanguage(); setLangDropdownOpen(false); }}
                                        style={{
                                            width: '100%', padding: '8px 14px', border: 'none', cursor: 'pointer',
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            fontSize: 13, fontWeight: 500, fontFamily: "'Plus Jakarta Sans', sans-serif",
                                            background: language === 'en' ? C.greenLight : C.surface,
                                            color: language === 'en' ? C.green : C.text,
                                        }}
                                    >
                                        English {language === 'en' && <span style={{ color: C.green }}>&#10003;</span>}
                                    </button>
                                    <button
                                        onClick={() => { if (language !== 'ar') toggleLanguage(); setLangDropdownOpen(false); }}
                                        style={{
                                            width: '100%', padding: '8px 14px', border: 'none', cursor: 'pointer',
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            fontSize: 13, fontWeight: 500, fontFamily: "'Plus Jakarta Sans', sans-serif",
                                            background: language === 'ar' ? C.greenLight : C.surface,
                                            color: language === 'ar' ? C.green : C.text,
                                        }}
                                    >
                                        العربية {language === 'ar' && <span style={{ color: C.green }}>&#10003;</span>}
                                    </button>
                                </div>
                            )}
                        </div>
                        {!isMobile && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{displayName}</span>
                                <span style={{ fontSize: 10, fontWeight: 700, color: C.green, background: C.greenLight, borderRadius: 4, padding: '2px 7px', letterSpacing: 0.3 }}>{roleLabel}</span>
                            </div>
                        )}
                        <div style={{ width: 30, height: 30, background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'white', flexShrink: 0 }}>
                            {userInitial}
                        </div>
                    </div>
                </div>

                {/* Page content */}
                <div style={{
                    flex: 1, overflow: 'auto',
                    padding: isMobile
                        ? `16px 16px calc(16px + ${MOBILE_TAB_BAR_HEIGHT}px + env(safe-area-inset-bottom))`
                        : 28,
                }}>
                    {activeFeature && enabledFeatures === null && isLoading
                        ? <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: C.mutedLight, fontSize: 14 }}>Loading…</div>
                        : featurePageLocked
                            ? <FeatureLocked feature={activeFeature} requiredPlan={FEATURE_PLAN[activeFeature]} />
                            : children}
                </div>
            </div>

            {isMobile && (
                <MobileTabBar
                    groups={filteredNavGroups}
                    onSignOut={logout}
                    helpCenterAllowed={hasPermission(NAV_PERMISSION_MAP['help-center'])}
                />
            )}

            <ChatPanel
                isOpen={chatOpen}
                isMinimised={chatMinimised}
                onClose={() => { setChatOpen(false); setChatMinimised(false); }}
                onMinimise={() => setChatMinimised(true)}
                onExpand={() => setChatMinimised(false)}
            />
        </div>
    );
};

export default PortalLayout;
